import { useEffect, useState } from "react";
import { Pencil, Play, Plus, Repeat, Trash2, X } from "lucide-react";
import api from "../lib/api";
import { money, shortDate, todayISO } from "../lib/format";
import { notify } from "../lib/toast";

const FREQ_LABELS = { daily: "Günlük", weekly: "Haftalık", monthly: "Aylık" };
const INCOME_CATS = ["Satış Geliri", "Danışmanlık", "Faiz Geliri", "Diğer Gelir"];
const EXPENSE_CATS = [
  "Ofis & Kira", "Personel", "Yazılım & Abonelik", "Vergi & Sigorta",
  "Pazarlama", "Kargo & Lojistik", "Diğer Gider",
];

function RecurringModal({ open, onClose, onSave, accounts, contacts, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    type: "expense",
    description: "",
    category: EXPENSE_CATS[0],
    amount: "",
    frequency: "monthly",
    start_date: todayISO(),
    account_id: "",
    contact_id: "",
    active: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          type: initial.type,
          description: initial.description,
          category: initial.category,
          amount: String(initial.amount),
          frequency: initial.frequency,
          start_date: initial.next_run || initial.start_date,
          account_id: initial.account_id || "",
          contact_id: initial.contact_id || "",
          active: initial.active !== false,
        });
      } else {
        setForm({
          type: "expense",
          description: "",
          category: EXPENSE_CATS[0],
          amount: "",
          frequency: "monthly",
          start_date: todayISO(),
          account_id: accounts[0]?.id || "",
          contact_id: "",
          active: true,
        });
      }
      setError("");
    }
  }, [open, initial, accounts]);

  if (!open) return null;

  const cats = form.type === "income" ? INCOME_CATS : EXPENSE_CATS;

  const submit = async () => {
    if (!form.description.trim()) return setError("Açıklama gerekli");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return setError("Geçerli bir tutar girin");
    setBusy(true);
    try {
      await onSave({ ...form, amount: amt });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="recurring-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isEdit ? "DÜZENLE" : "YENİ ŞABLON"}</span>
            <h2>{isEdit ? "Şablonu düzenle" : "Tekrarlayan işlem"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-recurring-modal"><X size={18} /></button>
        </div>
        <div className="segmented">
          <button
            className={form.type === "income" ? "active" : ""}
            onClick={() => setForm({ ...form, type: "income", category: INCOME_CATS[0] })}
            data-testid="recurring-type-income"
          >Gelir</button>
          <button
            className={form.type === "expense" ? "active" : ""}
            onClick={() => setForm({ ...form, type: "expense", category: EXPENSE_CATS[0] })}
            data-testid="recurring-type-expense"
          >Gider</button>
        </div>
        <label>
          Açıklama
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Örn. Ofis kirası"
            data-testid="recurring-description-input"
          />
        </label>
        <div className="form-grid">
          <label>
            Tutar (₺)
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="recurring-amount-input"
            />
          </label>
          <label>
            Sıklık
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              data-testid="recurring-frequency-select"
            >
              <option value="daily">Günlük</option>
              <option value="weekly">Haftalık</option>
              <option value="monthly">Aylık</option>
            </select>
          </label>
        </div>
        <div className="form-grid">
          <label>
            Kategori
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="recurring-category-select"
            >
              {cats.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label>
            İlk çalışma
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              data-testid="recurring-startdate-input"
            />
          </label>
        </div>
        <div className="form-grid">
          <label>
            Hesap
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              data-testid="recurring-account-select"
            >
              <option value="">Seçilmedi</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.bank} · {a.last_four}</option>)}
            </select>
          </label>
          <label>
            Cari
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              data-testid="recurring-contact-select"
            >
              <option value="">Seçilmedi</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-recurring-button">
          <Plus size={17} /> {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Şablonu kaydet"}
        </button>
      </div>
    </div>
  );
}

export default function RecurringPage() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, c] = await Promise.all([
        api.get("/recurring"),
        api.get("/accounts"),
        api.get("/contacts"),
      ]);
      setItems(r.data);
      setAccounts(a.data);
      setContacts(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runNow = async (rec) => {
    try {
      await api.post(`/recurring/${rec.id}/run`);
      notify.success(`${rec.description} çalıştırıldı`);
      load();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Çalıştırılamadı");
    }
  };

  const processAll = async () => {
    try {
      const r = await api.post("/recurring/process");
      if (r.data.generated > 0) notify.success(`${r.data.generated} işlem oluşturuldu`);
      else notify.info("Bugün için beklenen işlem yok");
      load();
    } catch {
      notify.error("İşleme başarısız");
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`"${rec.description}" şablonu silinsin mi?`)) return;
    try {
      await api.delete(`/recurring/${rec.id}`);
      notify.success("Şablon silindi");
      load();
    } catch {
      notify.error("Silme başarısız");
    }
  };

  const onSave = async (payload) => {
    if (editing) {
      await api.patch(`/recurring/${editing.id}`, payload);
      notify.success("Şablon güncellendi");
    } else {
      await api.post("/recurring", payload);
      notify.success("Şablon oluşturuldu");
    }
    await load();
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">OTOMASYON</div>
          <h1>Tekrarlayan İşlemler</h1>
          <p>Aylık kira, maaş gibi düzenli gelir/gider şablonlarını tanımlayın; VeraFin otomatik oluştursun.</p>
        </div>
        <div className="head-actions">
          <button className="secondary-btn" onClick={processAll} data-testid="process-recurring-button">
            <Play size={16} /> Zamanı gelenleri çalıştır
          </button>
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-recurring-button">
            <Plus size={17} /> Şablon ekle
          </button>
        </div>
      </div>

      <div className="panel inner-page" data-testid="recurring-panel">
        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="empty-state" data-testid="recurring-empty">
            <Repeat size={30} />
            <h3>Şablon yok</h3>
            <p>İlk tekrarlayan işleminizi oluşturarak başlayın.</p>
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-recurring-btn">
              <Plus size={14} /> Şablon ekle
            </button>
          </div>
        ) : (
          <div className="recurring-list">
            {items.map((r) => (
              <div className="recurring-row" key={r.id} data-testid={`recurring-${r.id}`}>
                <div className={`rec-badge ${r.type}`}>
                  <Repeat size={14} />
                </div>
                <div className="rec-main">
                  <strong>{r.description}</strong>
                  <span>
                    {r.category} · {FREQ_LABELS[r.frequency]} · Sonraki: {shortDate(r.next_run)}
                    {r.runs ? ` · ${r.runs} kez çalıştı` : ""}
                  </span>
                </div>
                <div className={`rec-amount ${r.type === "income" ? "income-text" : "expense-text"}`}>
                  {r.type === "income" ? "+" : "−"}{money(r.amount)}
                </div>
                <div className="rec-actions">
                  <button
                    className="icon-btn pay"
                    onClick={() => runNow(r)}
                    data-testid={`run-recurring-${r.id}`}
                    title="Şimdi çalıştır"
                  >
                    <Play size={13} />
                  </button>
                  <button
                    className="icon-btn ghost"
                    onClick={() => { setEditing(r); setModal(true); }}
                    data-testid={`edit-recurring-${r.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    className="icon-btn ghost"
                    onClick={() => remove(r)}
                    data-testid={`delete-recurring-${r.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <RecurringModal
        open={modal}
        onClose={closeModal}
        onSave={onSave}
        accounts={accounts}
        contacts={contacts}
        initial={editing}
      />
    </>
  );
}
